import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Plus, Trash2, Save } from 'lucide-react';
import axios from 'axios';

const FooterEditor = ({ BACKEND_URL }) => {
  const [footerSettings, setFooterSettings] = useState({
    sections: [],
    copyrightText: '',
    socialLinks: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFooterSettings();
  }, []);

  const fetchFooterSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/footer-settings`);
      setFooterSettings(response.data);
    } catch (error) {
      console.error('Error fetching footer settings:', error);
      // Set default if API fails
      setFooterSettings({
        sections: [
          {
            title: "Resources",
            links: [
              { text: "JEE Main Colleges", url: "/colleges/jee-main" },
              { text: "JEE Advanced Colleges", url: "/colleges/jee-advanced" },
              { text: "IIT Cutoffs", url: "/cutoffs/iit" },
              { text: "NIT Cutoffs", url: "/cutoffs/nit" },
              { text: "IIIT Cutoffs", url: "/cutoffs/iiit" },
              { text: "Blog", url: "/blog" }
            ]
          },
          {
            title: "Legal",
            links: [
              { text: "Privacy Policy", url: "/privacy" },
              { text: "Terms of Service", url: "/terms" }
            ]
          }
        ],
        copyrightText: "© 2024 College Kampus. All rights reserved.",
        socialLinks: {
          facebook: "https://facebook.com/collegekampus",
          twitter: "https://twitter.com/collegekampus",
          instagram: "https://instagram.com/collegekampus",
          linkedin: "https://linkedin.com/company/collegekampus"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/footer-settings`, footerSettings);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Footer settings saved successfully"
        });
      }
    } catch (error) {
      console.error('Error saving footer settings:', error);
      toast({
        title: "Error",
        description: "Failed to save footer settings",
        variant: "destructive"
      });
    }
  };

  const addSection = () => {
    setFooterSettings({
      ...footerSettings,
      sections: [
        ...footerSettings.sections,
        {
          title: "New Section",
          links: [{ text: "Link 1", url: "/link1" }]
        }
      ]
    });
  };

  const removeSection = (sectionIndex) => {
    const newSections = footerSettings.sections.filter((_, index) => index !== sectionIndex);
    setFooterSettings({
      ...footerSettings,
      sections: newSections
    });
  };

  const updateSectionTitle = (sectionIndex, title) => {
    const newSections = [...footerSettings.sections];
    newSections[sectionIndex].title = title;
    setFooterSettings({
      ...footerSettings,
      sections: newSections
    });
  };

  const addLink = (sectionIndex) => {
    const newSections = [...footerSettings.sections];
    newSections[sectionIndex].links.push({ text: "New Link", url: "/" });
    setFooterSettings({
      ...footerSettings,
      sections: newSections
    });
  };

  const removeLink = (sectionIndex, linkIndex) => {
    const newSections = [...footerSettings.sections];
    newSections[sectionIndex].links = newSections[sectionIndex].links.filter((_, index) => index !== linkIndex);
    setFooterSettings({
      ...footerSettings,
      sections: newSections
    });
  };

  const updateLink = (sectionIndex, linkIndex, field, value) => {
    const newSections = [...footerSettings.sections];
    newSections[sectionIndex].links[linkIndex][field] = value;
    setFooterSettings({
      ...footerSettings,
      sections: newSections
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading footer settings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Footer Settings</CardTitle>
        <CardDescription>Manage footer sections, links, and social media</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Footer Sections */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Footer Sections</h3>
            <Button onClick={addSection} size="sm" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Section
            </Button>
          </div>

          <div className="space-y-6">
            {footerSettings.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <Label>Section Title</Label>
                    <Input
                      value={section.title}
                      onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                      placeholder="e.g., Resources"
                    />
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => removeSection(sectionIndex)}
                    className="gap-2 mt-6"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Section
                  </Button>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Links</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addLink(sectionIndex)}
                      className="gap-2"
                    >
                      <Plus className="w-3 h-3" />
                      Add Link
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {section.links.map((link, linkIndex) => (
                      <div key={linkIndex} className="flex gap-2 items-end bg-white p-3 rounded border">
                        <div className="flex-1">
                          <Label className="text-xs">Link Text</Label>
                          <Input
                            value={link.text}
                            onChange={(e) => updateLink(sectionIndex, linkIndex, 'text', e.target.value)}
                            placeholder="e.g., JEE Main Colleges"
                            size="sm"
                          />
                        </div>
                        <div className="flex-1">
                          <Label className="text-xs">URL</Label>
                          <Input
                            value={link.url}
                            onChange={(e) => updateLink(sectionIndex, linkIndex, 'url', e.target.value)}
                            placeholder="e.g., /colleges/jee-main"
                            size="sm"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLink(sectionIndex, linkIndex)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Copyright Text */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Copyright & Legal</h3>
          <div className="space-y-2">
            <Label>Copyright Text</Label>
            <Input
              value={footerSettings.copyrightText}
              onChange={(e) => setFooterSettings({ ...footerSettings, copyrightText: e.target.value })}
              placeholder="© 2024 College Kampus. All rights reserved."
            />
          </div>
        </div>

        {/* Social Media Links */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Facebook URL</Label>
              <Input
                value={footerSettings.socialLinks.facebook}
                onChange={(e) => setFooterSettings({
                  ...footerSettings,
                  socialLinks: { ...footerSettings.socialLinks, facebook: e.target.value }
                })}
                placeholder="https://facebook.com/collegekampus"
              />
            </div>
            <div>
              <Label>Twitter URL</Label>
              <Input
                value={footerSettings.socialLinks.twitter}
                onChange={(e) => setFooterSettings({
                  ...footerSettings,
                  socialLinks: { ...footerSettings.socialLinks, twitter: e.target.value }
                })}
                placeholder="https://twitter.com/collegekampus"
              />
            </div>
            <div>
              <Label>Instagram URL</Label>
              <Input
                value={footerSettings.socialLinks.instagram}
                onChange={(e) => setFooterSettings({
                  ...footerSettings,
                  socialLinks: { ...footerSettings.socialLinks, instagram: e.target.value }
                })}
                placeholder="https://instagram.com/collegekampus"
              />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input
                value={footerSettings.socialLinks.linkedin}
                onChange={(e) => setFooterSettings({
                  ...footerSettings,
                  socialLinks: { ...footerSettings.socialLinks, linkedin: e.target.value }
                })}
                placeholder="https://linkedin.com/company/collegekampus"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} className="gap-2">
            <Save className="w-4 h-4" />
            Save Footer Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FooterEditor;
